import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useToast } from "@/components/feature/Toast";
import { uploadBlob } from "@/lib/profile";

type EditorType = "cover" | "avatar";

interface ImageEditorProps {
  open: boolean;
  onClose: () => void;
  title: string;
  type: EditorType;
  currentUrl: string | null;
  userId: string;
  onSave: (url: string | null) => Promise<void>;
}

const MAX_FILE_MB = 10;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

const DIMS: Record<EditorType, { w: number; h: number }> = {
  cover: { w: 1280, h: 400 },
  avatar: { w: 512, h: 512 },
};

/**
 * Editor modal para portada y foto de perfil: recorte (aspect ratio fijo),
 * zoom, rotación 90°, arrastre para reposicionar y vista previa en vivo.
 * La imagen se sube a Storage recién cuando el usuario pulsa "Guardar".
 */
export default function ImageEditor({
  open,
  onClose,
  title,
  type,
  currentUrl,
  userId,
  onSave,
}: ImageEditorProps) {
  const { showToast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    scale: number;
  } | null>(null);

  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { w, h } = DIMS[type];

  useEffect(() => {
    if (open) {
      setImg(null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setRotation(0);
      setError(null);
      setConfirmRemove(false);
    }
  }, [open, type]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);
    const rotated = rotation % 180 !== 0;
    const effW = rotated ? img.naturalHeight : img.naturalWidth;
    const effH = rotated ? img.naturalWidth : img.naturalHeight;
    const coverScale = Math.max(w / effW, h / effH);
    const s = coverScale * zoom;
    ctx.save();
    ctx.translate(w / 2 + offset.x, h / 2 + offset.y);
    ctx.rotate((rotation * Math.PI) / 180);
    const dw = img.naturalWidth * s;
    const dh = img.naturalHeight * s;
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }, [img, zoom, offset, rotation, w, h]);

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setError("Formato no permitido. Usá JPG, PNG o WEBP.");
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`La imagen supera el límite de ${MAX_FILE_MB} MB.`);
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setImg(image);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setRotation(0);
      setError(null);
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      setError("No se pudo leer la imagen. Probá con otro archivo.");
      URL.revokeObjectURL(url);
    };
    image.src = url;
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!img) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scale = rect.width / w;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: offset.x,
      origY: offset.y,
      scale: scale || 1,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    const dx = (e.clientX - dragRef.current.startX) / dragRef.current.scale;
    const dy = (e.clientY - dragRef.current.startY) / dragRef.current.scale;
    setOffset({
      x: dragRef.current.origX + dx,
      y: dragRef.current.origY + dy,
    });
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  function rotate() {
    setRotation((r) => (r + 90) % 360);
  }

  function reset() {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setRotation(0);
  }

  async function save() {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    setBusy(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/webp", 0.85)
      );
      if (!blob) throw new Error("No se pudo procesar la imagen.");
      const url = await uploadBlob(
        type === "avatar" ? "avatars" : "covers",
        userId,
        blob
      );
      await onSave(url);
      showToast(
        type === "avatar"
          ? "Foto actualizada correctamente."
          : "Portada actualizada correctamente.",
        "success"
      );
      onClose();
    } catch {
      showToast(
        "No pudimos procesar esta imagen. Intenta nuevamente.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await onSave(null);
      showToast(
        type === "avatar"
          ? "Foto de perfil eliminada."
          : "Portada eliminada.",
        "success"
      );
      setConfirmRemove(false);
      onClose();
    } catch {
      showToast(
        "No pudimos eliminar la imagen. Inténtalo nuevamente.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const saveLabel = type === "avatar" ? "Guardar foto" : "Guardar portada";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-background-200/70 px-5 py-4">
          <h2 className="font-heading text-base font-bold text-foreground-950">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-5 py-5">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />

          {img ? (
            <>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Vista previa
              </p>
              <div
                className={`relative cursor-move overflow-hidden bg-background-100 ${
                  type === "avatar"
                    ? "mx-auto h-56 w-56 rounded-full"
                    : "w-full rounded-lg"
                }`}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                <canvas
                  ref={canvasRef}
                  width={w}
                  height={h}
                  className={`block ${
                    type === "avatar" ? "h-full w-full" : "h-auto w-full"
                  }`}
                />
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-zoom-out-line"></i>
                </button>
                <span className="min-w-[52px] text-center text-sm font-semibold text-foreground-700">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-zoom-in-line"></i>
                </button>

                <span className="mx-1 h-6 w-px bg-background-200"></span>

                <button
                  type="button"
                  onClick={rotate}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-refresh-line"></i>
                  Rotar
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-restart-line"></i>
                  Restablecer
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-upload-line"></i>
                  Otra imagen
                </button>
              </div>

              <p className="mt-3 text-center text-xs text-foreground-400">
                Arrastrá la imagen para moverla dentro del recuadro.
              </p>
            </>
          ) : (
            <div
              className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-background-300 bg-background-100/50 px-6 py-10 text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-camera-line text-2xl"></i>
              </span>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-upload-line"></i>
                Cargar imagen
              </button>
              <p className="mt-3 text-xs text-foreground-500">
                o arrastrala y soltala acá
              </p>
              <p className="mt-2 text-xs text-foreground-400">
                JPG, PNG o WEBP · máx. {MAX_FILE_MB} MB
              </p>
            </div>
          )}

          {error && (
            <p className="mt-3 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">
              {error}
            </p>
          )}

          {confirmRemove && (
            <div className="mt-4 rounded-md border border-accent-200 bg-accent-100/50 p-4">
              <p className="text-sm text-accent-900">
                ¿Quieres eliminar tu{" "}
                {type === "avatar" ? "foto de perfil" : "foto de portada"}?
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRemove(false)}
                  className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={remove}
                  disabled={busy}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-accent-600 disabled:opacity-60"
                >
                  {busy && <i className="ri-loader-4-line animate-spin"></i>}
                  Eliminar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-background-200/70 px-5 py-4">
          {currentUrl && !confirmRemove ? (
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-background-50 px-3.5 py-2 text-sm font-semibold text-accent-700 transition-colors hover:bg-accent-100"
            >
              <i className="ri-delete-bin-line"></i>
              Eliminar
            </button>
          ) : (
            <span></span>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy || !img}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {busy && <i className="ri-loader-4-line animate-spin"></i>}
              {busy ? "Procesando imagen…" : saveLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}